import { CpgReport } from '../../../api/schema';
import { formatters, ValueInput } from '@utils/formattersV2';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { normalizeBarData } from '../MetricBarCard';

type MinimalCpgReport = Pick<
  CpgReport,
  | 'averagePricePerTransaction'
  | 'averagePricePerTransactionLift'
  | 'averagePricePerTransactionControl'
  | 'averagePurchaseFrequency'
  | 'averagePurchaseFrequencyLift'
  | 'averagePurchaseFrequencyControl'
  | 'penetration'
  | 'penetrationLift'
  | 'penetrationControl'
  | 'existingBuyerPercentage'
  | 'newBuyerPercentage'
  | 'existingBuyerSales'
  | 'newBuyerSales'
  | 'incrementalSalesLift'
  | 'incrementalSalesLiftConfidenceLevel'
  | 'reportDate'
>;

type PurchaseChartsPropsOptions = {
  reports: MinimalCpgReport[];
};

type PurchaseCardChartData = {
  title: string;
  lift: ValueInput;
  rows: ReturnType<typeof normalizeBarData>;
  valueFormatter?: (value: ValueInput) => string;
  confidenceLevel?: number;
};

type BuyerBreakdownData = {
  existingBuyerPercentage: number;
  newBuyerPercentage: number;
  existingBuyerSales: number;
  newBuyerSales: number;
};

type PurchaseChartsPropsReturn = {
  incrementalSalesLift: PurchaseCardChartData;
  price: PurchaseCardChartData;
  frequency: PurchaseCardChartData;
  penetration: PurchaseCardChartData;
  buyerBreakdown: BuyerBreakdownData;
};

export const purchaseChartsProps = (
  options: PurchaseChartsPropsOptions
): PurchaseChartsPropsReturn => {
  const sortedReports = options.reports.sort(
    (a, b) => Date.parse(a.reportDate) - Date.parse(b.reportDate)
  );
  const latestReport = sortedReports.at(-1);

  if (!latestReport) {
    return {
      incrementalSalesLift: {
        title: 'Incremental Sales Lift',
        lift: 0,
        confidenceLevel: 0,
        rows: [],
      },
      price: {
        title: 'Average Price per Transaction Lift',
        lift: 0,
        rows: [],
      },
      frequency: {
        title: 'Average Purchase Frequency Lift',
        lift: 0,
        rows: [],
      },
      penetration: {
        title: 'Penetration Lift',
        lift: 0,
        rows: [],
      },
      buyerBreakdown: {
        existingBuyerPercentage: 0,
        newBuyerPercentage: 0,
        existingBuyerSales: 0,
        newBuyerSales: 0,
      },
    };
  }

  return {
    incrementalSalesLift: {
      title: 'Incremental Sales Lift',
      lift: latestReport.incrementalSalesLift ?? 0,
      confidenceLevel:
        latestReport.incrementalSalesLiftConfidenceLevel ?? undefined,
      rows: [],
    },
    price: {
      title: 'Average Price per Transaction Lift',
      lift: latestReport.averagePricePerTransactionLift ?? 0,
      valueFormatter: value => formatters.currency(value, { decimals: 2 }),
      rows: normalizeBarData([
        {
          label: 'Exposed',
          value: latestReport.averagePricePerTransaction ?? 0,
          fillColor: unifiedPlatformColorPalette.chartDarkBlue,
        },
        {
          label: 'Control',
          value: latestReport.averagePricePerTransactionControl ?? 0,
          fillColor: unifiedPlatformColorPalette.chartDarkBlue,
        },
      ]),
    },
    frequency: {
      title: 'Average Purchase Frequency Lift',
      lift: latestReport.averagePurchaseFrequencyLift ?? 0,
      valueFormatter: value => formatters.number(value, { decimals: 1 }),
      rows: normalizeBarData([
        {
          label: 'Exposed',
          value: latestReport.averagePurchaseFrequency ?? 0,
          fillColor: unifiedPlatformColorPalette.chartDarkGreen,
        },
        {
          label: 'Control',
          value: latestReport.averagePurchaseFrequencyControl ?? 0,
          fillColor: unifiedPlatformColorPalette.chartDarkGreen,
        },
      ]),
    },
    penetration: {
      title: 'Penetration Lift',
      lift: latestReport.penetrationLift ?? 0,
      valueFormatter: value => formatters.percentage(value, { decimals: 2 }),
      rows: normalizeBarData([
        {
          label: 'Exposed',
          value: latestReport.penetration ?? 0,
          fillColor: unifiedPlatformColorPalette.chartDarkRed,
        },
        {
          label: 'Control',
          value: latestReport.penetrationControl ?? 0,
          fillColor: unifiedPlatformColorPalette.chartDarkRed,
        },
      ]),
    },
    buyerBreakdown: {
      existingBuyerPercentage: latestReport.existingBuyerPercentage ?? 0,
      newBuyerPercentage: latestReport.newBuyerPercentage ?? 0,
      existingBuyerSales: latestReport.existingBuyerSales ?? 0,
      newBuyerSales: latestReport.newBuyerSales ?? 0,
    },
  };
};
